<script setup lang="ts">
/**
 * Chapitre de la page publique d'une association, charte « apple » (Karim
 * 2026-10-18), sur le modèle de la rangée « Cagnotte associative : loi 1901,
 * fiscalité, reversement » de /cagnotte (CarrouselCartesApple) : grand titre
 * + nombre en gris, phrase d'appui, lien « Voir les N … › » à droite, puis
 * une rangée de grandes cartes qui DÉFILE — alignée à gauche sur la colonne
 * du site, elle déborde à droite de l'écran ; flèches ‹ › dessous, affichées
 * seulement s'il y a de quoi défiler.
 *
 * `fond` : gris (cartes blanches) ou blanc (cartes grises) — la page alterne
 * pour rythmer les chapitres. `defilant: false` : contenu posé dans la
 * colonne (carte seule, QR codes, infos…).
 * L'`id` reste celui de l'ancre de la barre des sections (`section-…`).
 */
import { TITRE_SECTION_APPLE } from "~/utils/titresApple";

const props = withDefaults(
  defineProps<{
    id: string;
    titre: string;
    /** Nombre total affiché en gris après le titre (masqué si 0 ou 1). */
    nombre?: number | null;
    texte?: string | null;
    /** Lien « tout voir » (page de liste) — ou `@voir` si c'est un bouton. */
    lienTexte?: string | null;
    lienHref?: string | null;
    boutonVoir?: boolean;
    fond?: "blanc" | "gris";
    defilant?: boolean;
  }>(),
  { nombre: null, texte: null, lienTexte: null, lienHref: null, boutonVoir: false, fond: "blanc", defilant: true },
);

defineEmits<{ voir: [] }>();

const { t } = useI18n();

// Flèches : visibles seulement si la rangée déborde ; grisées au bout.
const rangee = ref<HTMLElement | null>(null);
const debordant = ref(false);
const auDebut = ref(true);
const aLaFin = ref(false);

function mesurer() {
  const r = rangee.value;
  if (!r) return;
  debordant.value = r.scrollWidth > r.clientWidth + 4;
  auDebut.value = r.scrollLeft <= 4;
  aLaFin.value = r.scrollLeft + r.clientWidth >= r.scrollWidth - 4;
}

function defiler(sens: 1 | -1) {
  const r = rangee.value;
  const carte = r?.firstElementChild as HTMLElement | null;
  if (!r || !carte) return;
  r.scrollBy({ left: sens * (carte.offsetWidth + 20), behavior: "smooth" });
}

onMounted(() => {
  if (!props.defilant) return;
  mesurer();
  window.addEventListener("resize", mesurer, { passive: true });
});
onUnmounted(() => window.removeEventListener("resize", mesurer));
</script>

<template>
  <section
    :id="id"
    class="chapitre scroll-mt-20 overflow-hidden py-20 lg:py-28"
    :class="fond === 'gris' ? 'chapitre--gris bg-[#f5f5f7]' : 'chapitre--blanc bg-white'"
  >
    <div class="pa-reveal mx-auto mb-10 flex max-w-[1120px] flex-col gap-x-10 gap-y-4 px-4 sm:px-8 lg:mb-12 lg:flex-row lg:items-end lg:justify-between">
      <div class="max-w-[760px]">
        <h2 class="titre-chapitre text-[#1d1d1f] [text-wrap:balance]" :style="TITRE_SECTION_APPLE">
          {{ titre }}<span v-if="nombre && nombre > 1" class="ml-3 text-[#86868b]">{{ nombre }}</span>
        </h2>
        <p v-if="texte" class="mt-4 text-[17px] font-medium leading-[1.5] text-[#424245]">{{ texte }}</p>
      </div>
      <LienApple v-if="lienTexte && lienHref" :to="lienHref" class="shrink-0">{{ lienTexte }}</LienApple>
      <LienApple v-else-if="lienTexte && boutonVoir" class="shrink-0" @click="$emit('voir')">{{ lienTexte }}</LienApple>
    </div>

    <template v-if="defilant">
      <div ref="rangee" class="rangee pa-reveal flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4" @scroll.passive="mesurer">
        <slot />
      </div>
      <div v-show="debordant" class="mx-auto mt-6 flex max-w-[1120px] justify-end gap-3 px-4 sm:px-8">
        <button type="button" class="fleche" :disabled="auDebut" :aria-label="t('association.chapitre.precedent')" @click="defiler(-1)">
          <UIcon name="i-lucide-chevron-left" class="size-5" />
        </button>
        <button type="button" class="fleche" :disabled="aLaFin" :aria-label="t('association.chapitre.suivant')" @click="defiler(1)">
          <UIcon name="i-lucide-chevron-right" class="size-5" />
        </button>
      </div>
    </template>
    <div v-else class="mx-auto max-w-[1120px] px-4 sm:px-8">
      <slot />
    </div>
  </section>
</template>

<style scoped>
/* Mesures de la rangée de /cagnotte (relevées sur apple.com). */
.titre-chapitre {
  font-size: 32px;
}
@media (min-width: 735px) {
  .titre-chapitre {
    font-size: 48px;
  }
}
@media (min-width: 1069px) {
  .titre-chapitre {
    font-size: 56px;
  }
}

/* Couleur des cartes : l'inverse du fond du chapitre. */
.chapitre--gris {
  --fond-carte: #fff;
  /* Jamais le gris du chapitre : une carte sans image s'y fondrait. */
  --fond-image: #e8e8ed;
}
.chapitre--blanc {
  --fond-carte: #f5f5f7;
  --fond-image: #e8e8ed;
}

/* Rangée alignée sur la colonne de 1120 px, qui déborde à droite ; barre de
   défilement masquée (flèches et doigt). */
.rangee {
  padding-inline: max(1rem, calc((100vw - 1120px) / 2 + 2rem));
  scroll-padding-inline: max(1rem, calc((100vw - 1120px) / 2 + 2rem));
  scrollbar-width: none;
}
.rangee::-webkit-scrollbar {
  display: none;
}
@media (min-width: 640px) {
  .rangee {
    padding-inline: max(2rem, calc((100vw - 1120px) / 2 + 2rem));
    scroll-padding-inline: max(2rem, calc((100vw - 1120px) / 2 + 2rem));
  }
}

.fleche {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  background: #e8e8ed;
  color: #1d1d1f;
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;
}
.chapitre--blanc .fleche {
  background: #f5f5f7;
}
@media (hover: hover) {
  .fleche:not(:disabled):hover {
    background: #d2d2d7;
  }
}
.fleche:disabled {
  opacity: 0.4;
  cursor: default;
}
.fleche:focus-visible {
  outline: none;
  box-shadow: 0 0 0 4px color-mix(in oklab, var(--color-main), transparent 65%);
}
</style>
